import type {
  GameEvent,
  GameState,
  LogEntry,
  MapDefinition,
  Player,
  PlayerId,
  TerritoryId,
} from './types.js';

/**
 * Pure event → text rendering for the battle log. Called once per
 * GameEvent in a LogEntry's events array. The UI lays out the
 * resulting lines with a turn header above each turn group.
 *
 * Format choices (compact mode):
 *   Combat    "MAROON attacked PURPLE Asia W from Asia M  (3v2: −2/0)"
 *   Conquest  "MAROON captured Asia W from PURPLE"
 *   Deploy    "MAROON deployed 3 → NA SE"
 *   Advance   "MAROON advanced 4 units → Asia W"
 *   Fortify   "MAROON fortified 3 from Asia M → Asia W"
 *   Phase     "ATTACK PHASE"
 *   Turn      "TURN 4 — NAVY"
 *   Elim      "PURPLE eliminated"
 *   Won       "T2 victory — MAROON wins"
 *   Undo      "MAROON undid deploy of 3 to NA SE"
 *
 * Most events fit on one line. Plain ASCII (no emoji); the team /
 * player formatting is left to the renderer that knows the player
 * palette.
 */

interface LookupCtx {
  readonly playerById: ReadonlyMap<PlayerId, Player>;
  readonly territoryNameById: ReadonlyMap<TerritoryId, string>;
}

export function buildLookupContext(
  state: GameState,
  map: MapDefinition
): LookupCtx {
  return {
    playerById: new Map(state.players.map((p) => [p.id, p])),
    territoryNameById: new Map(
      map.territories.map((t) => [t.id, t.name])
    ),
  };
}

function playerName(ctx: LookupCtx, id: PlayerId): string {
  const p = ctx.playerById.get(id);
  return p ? p.name.toUpperCase() : 'UNKNOWN';
}

function territoryName(ctx: LookupCtx, id: TerritoryId): string {
  return ctx.territoryNameById.get(id) ?? String(id);
}

/**
 * Render one event to a single text line. Returns null for events
 * the log intentionally suppresses (currently nothing — every event
 * gets a line). Caller can filter.
 */
export function formatEvent(
  event: GameEvent,
  ctx: LookupCtx
): string {
  switch (event.kind) {
    case 'deployed':
      return `${playerName(ctx, event.playerId)} deployed ${event.units} → ${territoryName(ctx, event.territory)}`;
    case 'deployUndone':
      return `Undid deploy of ${event.units} to ${territoryName(ctx, event.territory)}`;
    case 'combatResolved': {
      const r = event.result;
      const att = r.roll.attackerDice.length;
      const def = r.roll.defenderDice.length;
      const tag = r.conquered ? ' conquered' : '';
      return `Attack ${territoryName(ctx, r.from)} → ${territoryName(ctx, r.to)}  (${att}v${def}: −${r.attackerLosses}/−${r.defenderLosses}${tag})`;
    }
    case 'territoryCaptured':
      return `${playerName(ctx, event.to)} captured ${territoryName(ctx, event.territory)} from ${playerName(ctx, event.from)}`;
    case 'advanced':
      return `Advanced ${event.units} → ${territoryName(ctx, event.to)}`;
    case 'fortified':
      return `Fortified ${event.units} from ${territoryName(ctx, event.from)} → ${territoryName(ctx, event.to)}`;
    case 'phaseChanged':
      return `${event.phase.toUpperCase()} PHASE`;
    case 'turnEnded':
      return `TURN ${event.turnNumber} → ${playerName(ctx, event.nextPlayer)}`;
    case 'playerEliminated':
      return `${playerName(ctx, event.playerId)} eliminated`;
    case 'gameWon':
      return `${playerName(ctx, event.winner)} wins`;
    case 'turnSkipped':
      // v0.9.9 Hourglass — the count contextualizes severity:
      // "1/3" warns it's a habit, "3/3" marks the forfeit threshold.
      return `${playerName(ctx, event.playerId)} forfeited their turn (${event.consecutiveCount}/3)`;
    case 'turnForfeited':
      return `${playerName(ctx, event.playerId)} forfeited the game (timeout)`;
  }
}

/**
 * Group entries by their `turnNumber` for UI rendering. Returned
 * groups are in the same order as the input entries (so reversing
 * the input gives newest-turn-first output).
 */
export interface TurnGroup {
  readonly turnNumber: number;
  readonly entries: readonly LogEntry[];
}

export function groupByTurn(
  entries: readonly LogEntry[]
): readonly TurnGroup[] {
  if (entries.length === 0) return [];
  const groups: TurnGroup[] = [];
  let current: { turnNumber: number; entries: LogEntry[] } | null = null;
  for (const e of entries) {
    if (!current || current.turnNumber !== e.turnNumber) {
      if (current) groups.push(current);
      current = { turnNumber: e.turnNumber, entries: [e] };
    } else {
      current.entries.push(e);
    }
  }
  if (current) groups.push(current);
  return groups;
}
